import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/ApiClient';
import CopyButton from '../../components/CopyButton';

const F = { width: '100%', boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-2)', marginBottom: 6 };

function Section({ title, children }) {
  return (
    <div className="card" style={{ padding: 24, marginBottom: 16 }}>
      <h2 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 20, paddingBottom: 12, borderBottom: '1px solid var(--border)' }}>{title}</h2>
      {children}
    </div>
  );
}

function Msg({ text }) {
  if (!text) return null;
  const ok = text.startsWith('✓');
  return (
    <div style={{ fontSize: 13, padding: '9px 12px', borderRadius: 6, marginTop: 10,
      background: ok ? '#F0FDF4' : 'var(--danger-bg)', color: ok ? '#16A34A' : 'var(--danger)',
      border: `1px solid ${ok ? '#BBF7D0' : '#FECACA'}` }}>
      {text}
    </div>
  );
}

export default function Settings() {
  const { user, updateUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [avatar, setAvatar] = useState(user?.avatar || '');
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [balanceAmt, setBalanceAmt] = useState('');
  const [msgs, setMsgs] = useState({});

  // API Keys
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyExpiry, setNewKeyExpiry] = useState('');
  const [createdKey, setCreatedKey] = useState(null);

  useEffect(() => {
    api.listApiKeys().then(d => setApiKeys(d.keys || [])).catch(() => {});
  }, []);

  const createKey = async (e) => {
    e.preventDefault();
    try {
      const res = await api.createApiKey({
        name: newKeyName,
        expiresInDays: newKeyExpiry || undefined,
        scopes: ['read', 'download'],
      });
      setCreatedKey(res.key);
      setNewKeyName(''); setNewKeyExpiry('');
      api.listApiKeys().then(d => setApiKeys(d.keys || []));
    } catch (err) { alert(err?.response?.data?.message || 'Failed'); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this key?')) return;
    try {
      await api.revokeApiKey(id);
      setApiKeys(prev => prev.filter(k => k._id !== id));
    } catch { alert('Failed'); }
  };

  const setMsg = (k, v) => { setMsgs(m => ({ ...m, [k]: v })); setTimeout(() => setMsgs(m => ({ ...m, [k]: '' })), 4000); };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      const res = await api.updateMe({ name, avatar });
      updateUser(res.user);
      setMsg('profile', '✓ Profile updated');
    } catch (err) { setMsg('profile', err?.response?.data?.message || 'Update failed'); }
  };

  const changePw = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setMsg('pw', 'Passwords do not match'); return; }
    try {
      await api.changePassword({ oldPassword: oldPw, newPassword: newPw });
      setOldPw(''); setNewPw(''); setConfirmPw('');
      setMsg('pw', '✓ Password changed');
    } catch (err) { setMsg('pw', err?.response?.data?.message || 'Failed'); }
  };

  const topUp = async (e) => {
    e.preventDefault();
    const amt = Number(balanceAmt);
    if (!amt || amt <= 0) { setMsg('bal', 'Enter a valid amount'); return; }
    try {
      const res = await api.updateMe({ balance: (user.balance || 0) + amt });
      updateUser({ balance: res.user?.balance });
      setBalanceAmt('');
      setMsg('bal', `✓ Added $${amt} to balance`);
    } catch (err) { setMsg('bal', err?.response?.data?.message || 'Failed'); }
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.5px', marginBottom: 24 }}>Settings</h1>

      {/* Profile */}
      <Section title="Profile">
        <form onSubmit={saveProfile} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--accent-bg)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, fontWeight: 700, flexShrink: 0, overflow: 'hidden', border: '1px solid var(--border)' }}>
              {avatar ? <img src={avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : (user?.name || 'U')[0]}
            </div>
            <div style={{ flex: 1 }}>
              <label style={lbl}>Avatar URL</label>
              <input value={avatar} onChange={e => setAvatar(e.target.value)} className="field" style={F} placeholder="https://…" />
            </div>
          </div>
          <div>
            <label style={lbl}>Display name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="field" style={F} />
          </div>
          <div>
            <label style={lbl}>Email</label>
            <input value={user?.email || ''} disabled className="field" style={{ ...F, opacity: 0.6, cursor: 'not-allowed' }} />
          </div>
          <Msg text={msgs.profile} />
          <button type="submit" className="btn-primary" style={{ alignSelf: 'flex-start', fontSize: 13 }}>Save changes</button>
        </form>
      </Section>

      {/* Password */}
      <Section title="Change Password">
        <form onSubmit={changePw} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[['Current password', oldPw, setOldPw], ['New password', newPw, setNewPw], ['Confirm new password', confirmPw, setConfirmPw]].map(([l, v, s]) => (
            <div key={l}>
              <label style={lbl}>{l}</label>
              <input type="password" value={v} onChange={e => s(e.target.value)} required className="field" style={F} />
            </div>
          ))}
          <Msg text={msgs.pw} />
          <button type="submit" className="btn-secondary" style={{ alignSelf: 'flex-start', fontSize: 13 }}>Change password</button>
        </form>
      </Section>

      {/* Balance */}
      <Section title="Balance">
        <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--success)', fontFamily: 'Space Mono', letterSpacing: '-1px', marginBottom: 16 }}>
          ${(user?.balance || 0).toFixed(2)}
        </div>
        <form onSubmit={topUp} style={{ display: 'flex', gap: 10 }}>
          <input type="number" min="1" placeholder="Amount" value={balanceAmt} onChange={e => setBalanceAmt(e.target.value)} className="field" style={{ maxWidth: 160 }} />
          <button type="submit" style={{ padding: '9px 20px', borderRadius: 6, background: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'inherit' }}>
            Top up
          </button>
        </form>
        <Msg text={msgs.bal} />
      </Section>

      {/* API Keys */}
      <Section title="API Keys">
        <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 16, lineHeight: 1.6 }}>
          Use API keys to access datasets programmatically — in Python, curl, Jupyter, or any HTTP client.
        </p>

        {/* Show newly created key ONCE */}
        {createdKey && (
          <div style={{ padding: '14px 16px', borderRadius: 8, background: '#F0FDF4', border: '1px solid #BBF7D0', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#16A34A', marginBottom: 8 }}>
              Key created — copy it now, it won't be shown again
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{ flex: 1, fontFamily: 'Space Mono', fontSize: 12, background: 'white', padding: '7px 10px', borderRadius: 6, border: '1px solid #BBF7D0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {createdKey.rawKey}
              </code>
              <CopyButton text={createdKey.rawKey} label="Copy" size="sm" />
            </div>
            <button onClick={() => setCreatedKey(null)} style={{ marginTop: 10, background: 'none', border: 'none', fontSize: 12, color: '#16A34A', cursor: 'pointer', padding: 0 }}>
              I've saved it ✓
            </button>
          </div>
        )}

        {/* Existing keys */}
        {apiKeys.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            {apiKeys.map(k => (
              <div key={k._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{k.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', fontFamily: 'Space Mono' }}>
                    {k.keyPrefix} · {k.scopes?.join(', ')}
                    {k.lastUsedAt && ` · last used ${new Date(k.lastUsedAt).toLocaleDateString()}`}
                    {k.expiresAt && ` · expires ${new Date(k.expiresAt).toLocaleDateString()}`}
                  </div>
                </div>
                <button onClick={() => revokeKey(k._id)} style={{ padding: '4px 10px', borderRadius: 5, background: 'var(--danger-bg)', color: 'var(--danger)', border: '1px solid #FECACA', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>
                  Revoke
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Create new key */}
        <form onSubmit={createKey} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <label style={lbl}>Key name</label>
            <input value={newKeyName} onChange={e => setNewKeyName(e.target.value)} required className="field" style={{ width: 200 }} placeholder="e.g. My Colab script" />
          </div>
          <div>
            <label style={lbl}>Expires in (days, optional)</label>
            <input type="number" min="1" value={newKeyExpiry} onChange={e => setNewKeyExpiry(e.target.value)} className="field" style={{ width: 140 }} placeholder="Never" />
          </div>
          <button type="submit" className="btn-primary" style={{ fontSize: 13, padding: '9px 18px' }}>
            Generate key
          </button>
        </form>
      </Section>
    </div>
  );
}
